import {
  Box,
  Button,
  Typography,
  useTheme,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Alert,
  TablePagination,
  tableCellClasses,
  styled,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  MenuItem,
} from "@mui/material";
import { tokens } from "../../theme";
import Calendar from "../calendar/calendar";
import { useMemo, useState } from "react";
import React, { useEffect } from "react";
import appointmentService from "../../services/appointmentService"; // Import the service
import { format } from "date-fns";
import Refresh from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import { useLocation } from "react-router-dom";
import { getBilledTests, getOrgPathLabReportsFull } from "../../services/pathLabTestReportService";
import dayjs from "dayjs";
import { downloadStyledTableExcel } from "../../utils/patientTableExcelExport";
import AddNewLabTest from "../path-lab/AddNewLabTest";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import EditLabTest from "../path-lab/EditLabTest";
import { getPrescriptionsWithLabTest, getOpdLabTests } from "../../services/prescriptionService";
import { aggregatePathLabReportsFromPrescriptions } from "./labTestWorkflowUtils";
import { dedupePathLabReportRecords, normalizePathLabReportFields } from "../../utils/labTestDedupeUtils";
import { listLabBookingsForPatients } from "../../services/patientLabBookingService";
import {
  buildMissingPatientBookedTestRows,
  createPatientBookedTestMatcher,
} from "../../utils/patientBookedLabTest";
import BookedByPatientMark from "./components/BookedByPatientMark";

const columns = [
  { id: "reportId", label: "Report Id" },
  { id: "testName", label: "Test Name" },
  { id: "patientName", label: "Patient Name" },
  { id: "result", label: "Result" },
  { id: "normalRange", label: "Normal Range" },
  { id: "reportDate", label: "Report Date" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));
const AllPatientTest = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetching, setFetching] = useState({ path_lab_test: true });
  const [path_lab_test, setPathLabTestList] = useState([]);
  const [sourcePrescriptions, setSourcePrescriptions] = useState([]);
  const [patientBookings, setPatientBookings] = useState([]);
  const [lab_test_info, setLabTestInfo] = useState({});
  const theme = useTheme();

  const [open_lab_test_modal, setOpenLabReportLest] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPatientName, setFilterPatientName] = useState("");
  const [filterTestName, setFilterTestName] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [isTestBookedByPatient, setIsTestBookedByPatient] = useState(() => () => false);

  useEffect(() => {
    let active = true;
    const patientIds = [
      ...(path_lab_test || []).map((row) => row?.patientId ?? row?.patient_id),
      ...(sourcePrescriptions || []).map((row) => row?.patientId ?? row?.patient_id),
    ];
    listLabBookingsForPatients(patientIds)
      .then((bookings) => {
        if (!active) return;
        setPatientBookings(Array.isArray(bookings) ? bookings : []);
        setIsTestBookedByPatient(() =>
          createPatientBookedTestMatcher(bookings, sourcePrescriptions)
        );
      })
      .catch(() => {
        if (active) setIsTestBookedByPatient(() => () => false);
      });
    return () => {
      active = false;
    };
  }, [path_lab_test, sourcePrescriptions]);

  const getReportStatus = (row) =>
    String(row?.resultStatus ?? row?.result_status ?? row?.status ?? "").toUpperCase();

  useEffect(() => {
    fetchPathLabTestList();
  }, []);

  const fetchPathLabTestList = async () => {
    setFetching({ ...fetching, path_lab_test: true });
    try {
      const buildMergedList = (directReports, prescriptions) => {
        const prescriptionReports = aggregatePathLabReportsFromPrescriptions(prescriptions || []);
        const mergedReports = dedupePathLabReportRecords([
          ...(Array.isArray(directReports) ? directReports : []),
          ...(Array.isArray(prescriptionReports) ? prescriptionReports : []),
        ]).map((row) => normalizePathLabReportFields(row));

        const visibleReports = mergedReports.filter((row) => {
          const status = getReportStatus(row);
          return status !== "REJECT" && status !== "REJECTED";
        });

        return [...visibleReports].sort(
          (a, b) => (b.reportId ?? b.id ?? 0) - (a.reportId ?? a.id ?? 0)
        );
      };

      const prescriptionsPrimary = await getPrescriptionsWithLabTest().catch(() => []);
      const prescriptions =
        Array.isArray(prescriptionsPrimary) && prescriptionsPrimary.length
          ? prescriptionsPrimary
          : await getOpdLabTests().catch(() => []);

      setSourcePrescriptions(Array.isArray(prescriptions) ? prescriptions : []);
      setPathLabTestList(buildMergedList([], prescriptions));

      // Stage 1: billed tests (fast).
      getBilledTests()
        .then((billed) => {
          if (!Array.isArray(billed) || billed.length === 0) return;
          setPathLabTestList(buildMergedList(billed, prescriptions));
        })
        .catch(() => {});

      // Stage 2: full org list via GET /pathlabreports (no completed-tests).
      getOrgPathLabReportsFull()
        .then((directReports) => {
          if (!Array.isArray(directReports) || directReports.length === 0) return;
          setPathLabTestList(buildMergedList(directReports, prescriptions));
        })
        .catch(() => {});
    } catch (error) {
      console.log(error);
    }
    setFetching({ ...fetching, path_lab_test: false });
  };

  const rowsWithPatientBookings = useMemo(() => {
    const extras = buildMissingPatientBookedTestRows(
      patientBookings,
      sourcePrescriptions,
      path_lab_test
    );
    return [...(path_lab_test || []), ...extras];
  }, [path_lab_test, patientBookings, sourcePrescriptions]);

  const filteredRows = (rowsWithPatientBookings || [])
    .filter((row) => {
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const vals = [row.reportId, row.testName, row.patientName, row.result, row.normalRange, row.reportDate]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase());
        if (!vals.some((v) => v.includes(q))) return false;
      }
      if (filterPatientName.trim()) {
        if (!String(row.patientName || "").toLowerCase().includes(filterPatientName.toLowerCase())) return false;
      }
      if (filterTestName.trim()) {
        if (!String(row.testName || "").toLowerCase().includes(filterTestName.toLowerCase())) return false;
      }
      if (fromDate || toDate) {
        const d = row.reportDate ? new Date(row.reportDate) : null;
        if (!d) return false;
        if (fromDate && d < new Date(fromDate)) return false;
        if (toDate) {
          const toEnd = new Date(toDate);
          toEnd.setHours(23, 59, 59, 999);
          if (d > toEnd) return false;
        }
      }
      return true;
    })
    .sort((a, b) => (b.reportId ?? b.id ?? 0) - (a.reportId ?? a.id ?? 0));

  const clearFilters = () => {
    setSearchQuery("");
    setFilterPatientName("");
    setFilterTestName("");
    setFromDate("");
    setToDate("");
    setPage(0);
  };

  const handleExport = async () => {
    const rows = filteredRows || [];
    const headers = ["Report Id", "Test Name", "Patient Name", "Result", "Normal Range", "Report Date"];
    const tableRows = rows.map((r) => [
      r.reportId ?? r.id,
      isTestBookedByPatient(r) || r.bookedByPatient
        ? `${r.testName || ""} (Booked by patient)`
        : r.testName,
      r.patientName,
      r.result,
      r.normalRange,
      r.reportDate ? dayjs(r.reportDate).format("YYYY-MM-DD") : "",
    ]);

    await downloadStyledTableExcel({
      filename: `all_patient_tests_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "All Patient Tests",
      title: "All Patient Tests",
      tableHeaders: headers,
      tableRows,
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <Grid container>
      <Grid
        item
        xs="12"
        display={"flex"}
        justifyContent={"space-between"}
        // mb={"5px"}
      >
        <Grid>
          <Typography variant="h4" fontWeight={"bold"}>
            All Patient Test
          </Typography>
        </Grid>
        <Box display={"flex"} alignItems={"center"}>
          <IconButton onClick={async () => await fetchPathLabTestList()}>
            <Refresh />
          </IconButton>
        </Box>
      </Grid>
      <Paper sx={{ width: "100%" }}>
        <Box sx={{ p: 2, pt: 1 }}>
          <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
              <IconButton onClick={() => setShowFilters((v) => !v)}>
                <FilterAltIcon color={showFilters ? "primary" : "inherit"} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <IconButton onClick={handleExport}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          {showFilters && (
          <Stack spacing={1.5} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1 }}>
            <TextField label="Patient name" value={filterPatientName} onChange={(e)=>setFilterPatientName(e.target.value)} />
            <TextField label="Test name" value={filterTestName} onChange={(e)=>setFilterTestName(e.target.value)} />
            <TextField label="From date" type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} InputLabelProps={{shrink:true}} />
            <TextField label="To date" type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} InputLabelProps={{shrink:true}} />
            <Tooltip title="Clear filters">
              <IconButton onClick={clearFilters}>
                <ClearAllIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          )}
        </Box>
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell
                    key={column.id}
                    align={column.align}
                    style={{ top: 57, minWidth: column.minWidth }}
                  >
                    {column.label}
                  </StyledTableCell>
                ))}
                <StyledTableCell key={"action"} style={{ top: 57 }}>
                  Action
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 || fetching.path_lab_test ? (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    {fetching.path_lab_test && <CircularProgress size={40} />}
                    {!fetching.path_lab_test && filteredRows.length === 0 && (
                      <Alert>No Test Record </Alert>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row.code}
                      >
                        {columns.map((column) => {
                          const value = row[column.id];
                          if (column.id === "reportDate") {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                {dayjs(value).format("D MMM YYYY")}
                              </TableCell>
                            );
                          } else if (column.id === "testName") {
                            const booked = isTestBookedByPatient(row) || (row.bookedByPatient ? { whenLabel: row.preferredTime || "" } : null);
                            return (
                              <TableCell key={column.id} align={column.align}>
                                {value}
                                {booked ? <BookedByPatientMark detail={booked.whenLabel} /> : null}
                              </TableCell>
                            );
                          } else {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                {column.format && typeof value === "number"
                                  ? column.format(value)
                                  : value}
                              </TableCell>
                            );
                          }
                        })}
                        <TableCell key={row.code}>
                          <Box>
                            <IconButton
                              sx={{ color: theme.palette.primary.main }}
                              onClick={() => {
                                setOpenLabReportLest(true);
                                setLabTestInfo(row);
                              }}
                            >
                              <ModeEditOutlineTwoToneIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <EditLabTest
        open={open_lab_test_modal}
        onClose={async () => {
          setOpenLabReportLest(false);
          setLabTestInfo(null);
          await fetchPathLabTestList();
        }}
        lab_report={lab_test_info}
      />
    </Grid>
  );
};

export default AllPatientTest;
